import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, useParams } from 'react-router-dom';
import api from '../../api';
import { usePusher } from '../../contexts/PusherContext';
import { buildCardFieldRows } from './cardFieldRows';
import { cardRevealErrorMessage } from './cardRevealError';
import ComponentErrorBoundary from '../common/ComponentErrorBoundary';
import {
  credentialFieldText,
  maskCredentialValue,
} from '../../utils/credentialFieldValue';
import './intervention-operator-page.scss';

/**
 * Dream-hosted operator page for a browser intervention.
 *
 * Embeds the hosted browser's live view and wraps it with the same controls
 * the Electron viewer draws over its screencast: Done / Couldn't do / Feedback,
 * credential reveal, mandate-gated card details and MFA requests. The live
 * view URL is fetched from the ops-only operator-view route and only ever
 * lives in this component's state — it is not part of the page URL.
 */

interface OperatorIntervention {
  commandId: string;
  sessionId: string;
  threadId: string;
  userId: string;
  message: string;
  status: 'pending' | 'active' | 'resolved';
  createdAt: number;
  attempt: number;
  provider: 'browserbase' | 'electron';
  hasCredentials: boolean;
  requiresCardEntry: boolean;
  mandate: {
    id: string;
    brief: string;
    spendPermission: string | null;
    spendCurrency: string | null;
    paymentMethod: string | null;
  } | null;
  requesterName: string | null;
}

interface OperatorViewResponse {
  intervention: OperatorIntervention;
  liveViewUrl: string | null;
}

interface RevealedCredential {
  credentialId: string;
  label: string;
  site: string | null;
  credentialType: string;
  fields: Record<string, unknown> | null;
  error?: string;
}

interface CardDetailsResponse {
  success: boolean;
  paymentMethod?: 'agent_card' | 'user_card' | 'merchant_stored';
  error?: string;
  cardHint?: string | null;
  message?: string;
  alreadyIssued?: boolean;
  cardDetails?: {
    cardNumber?: string;
    expiryMonth?: string;
    expiryYear?: string;
    cvc?: string;
    nameOnCard?: string | null;
    cardNetwork?: string;
    label?: string;
    cardLastFour?: string;
    fields?: Record<string, string> | null;
    cvvMissing?: boolean;
    cardholderNameAssumed?: boolean;
  } | null;
}

type LoginTfaType =
  | 'sms_code'
  | 'email_code'
  | 'authenticator_code'
  | 'push_approval'
  | 'other';

const LOGIN_TFA_LABELS: Record<LoginTfaType, string> = {
  sms_code: 'SMS code',
  email_code: 'Email code',
  authenticator_code: 'Authenticator app',
  push_approval: 'Push approval',
  other: 'Other',
};

type MfaKind = 'login' | 'payment_3ds';

interface MfaState {
  requestId: string | null;
  kind: MfaKind;
  status: 'requesting' | 'pending' | 'code' | 'awaiting-user' | 'error';
  code?: string;
  detail?: string;
  cancelling?: boolean;
  cancelError?: string;
}

const TERMINAL_MFA_STATUSES: ReadonlySet<MfaState['status']> = new Set<
  MfaState['status']
>(['code', 'awaiting-user']);

/**
 * Whether a Pusher MFA event belongs to the request on screen. Events can
 * arrive before the request POST has returned its requestId, in which case
 * the state has no id yet and the event is provisionally accepted; the POST
 * response then reconciles (see applyMfaRequestResponse).
 */
const mfaEventMatches = (prev: MfaState, eventRequestId?: string) =>
  !eventRequestId ||
  prev.requestId === null ||
  prev.requestId === eventRequestId;

const applyMfaRequestResponse = (
  prev: MfaState | null,
  next: { requestId: string | null; kind: MfaKind; detail: string },
): MfaState | null => {
  if (!prev || prev.kind !== next.kind) return prev;
  const sameRequest =
    prev.requestId === null ||
    next.requestId === null ||
    prev.requestId === next.requestId;
  if (sameRequest && TERMINAL_MFA_STATUSES.has(prev.status)) {
    // The user already answered before the POST came back: keep the code.
    return { ...prev, requestId: next.requestId ?? prev.requestId };
  }
  return { ...next, status: 'pending' };
};

type OutcomeKind = 'completed' | 'could_not' | 'feedback';

const maskValue = maskCredentialValue;

const formatAge = (createdAt: number) => {
  const mins = Math.max(0, Math.round((Date.now() - createdAt) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
};

const InterventionOperatorPage: React.FC = () => {
  const { commandId = '' } = useParams<{ commandId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true) as {
    pusherInstance?: {
      subscribe: (name: string) => {
        bind: (event: string, cb: (data: unknown) => void) => void;
        unbind_all: () => void;
      };
      unsubscribe: (name: string) => void;
    } | null;
  } | null;

  const [view, setView] = useState<OperatorViewResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [outcomeMode, setOutcomeMode] = useState<
    'idle' | 'could_not' | 'feedback'
  >('idle');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState<OutcomeKind | null>(null);
  const [resolvedAs, setResolvedAs] = useState<OutcomeKind | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [creds, setCreds] = useState<RevealedCredential[] | null>(null);
  const [credLoading, setCredLoading] = useState(false);
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());

  const [card, setCard] = useState<CardDetailsResponse | null>(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);

  const [mfa, setMfa] = useState<MfaState | null>(null);
  // Bumped whenever an MFA request starts or is dismissed so a POST response
  // that lands after the operator moved on is ignored (and cancelled).
  const mfaSeq = useRef(0);
  const [loginTfaType, setLoginTfaType] = useState<LoginTfaType>('sms_code');
  const [showLoginPicker, setShowLoginPicker] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const load = useCallback(async () => {
    if (!commandId) return;
    try {
      setLoading(true);
      setLoadError(null);
      const response = await api.get<OperatorViewResponse>(
        `/browser-agent/interventions/${encodeURIComponent(commandId)}/operator-view`,
        await authHeaders(),
      );
      setView(response.data);
    } catch (err: any) {
      const status = err?.response?.status;
      setLoadError(
        status === 404
          ? 'This intervention has already been resolved or has expired.'
          : status === 403
            ? 'Ops access is required to view interventions.'
            : err?.response?.data?.error || 'Could not load the intervention.',
      );
    } finally {
      setLoading(false);
    }
  }, [commandId, authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  // MFA codes come back on the browser-session channel, same as for the
  // Electron viewer.
  const sessionId = view?.intervention.sessionId;
  useEffect(() => {
    const pusherInstance = pusherContext?.pusherInstance;
    if (!pusherInstance || !sessionId) return;
    const channelName = `browser-session-${sessionId}`;
    const channel = pusherInstance.subscribe(channelName);
    channel.bind('tfa-code-result', (raw: unknown) => {
      const data = raw as { requestId?: string; code?: string };
      setMfa((prev) =>
        prev && prev.status !== 'error' && mfaEventMatches(prev, data.requestId)
          ? {
              ...prev,
              requestId: prev.requestId ?? data.requestId ?? null,
              status: 'code',
              code: data.code ?? '',
            }
          : prev,
      );
    });
    channel.bind('tfa-awaiting-user', (raw: unknown) => {
      const data = raw as { requestId?: string };
      setMfa((prev) =>
        prev &&
        prev.status !== 'error' &&
        prev.status !== 'code' &&
        mfaEventMatches(prev, data.requestId)
          ? {
              ...prev,
              requestId: prev.requestId ?? data.requestId ?? null,
              status: 'awaiting-user',
            }
          : prev,
      );
    });
    return () => {
      channel.unbind_all();
      pusherInstance.unsubscribe(channelName);
    };
  }, [pusherContext?.pusherInstance, sessionId]);

  const submitOutcome = useCallback(
    async (outcome: OutcomeKind) => {
      if (submitting) return;
      setActionError(null);
      const trimmed = note.trim();
      if (outcome === 'feedback' && !trimmed) {
        setActionError('Write the guidance you want the agent to follow.');
        return;
      }
      try {
        setSubmitting(outcome);
        await api.post(
          `/browser-agent/interventions/${encodeURIComponent(commandId)}/outcome`,
          { outcome, note: outcome === 'completed' ? undefined : trimmed },
          await authHeaders(),
        );
        setResolvedAs(outcome);
      } catch (err: any) {
        const status = err?.response?.status;
        setActionError(
          status === 409
            ? 'Someone already resolved this intervention.'
            : err?.response?.data?.error || 'Could not hand back to the agent.',
        );
      } finally {
        setSubmitting(null);
      }
    },
    [commandId, note, submitting, authHeaders],
  );

  const revealCredentials = useCallback(async () => {
    try {
      setCredLoading(true);
      setActionError(null);
      const response = await api.get(
        `/browser-agent/interventions/${encodeURIComponent(commandId)}/credentials/reveal`,
        await authHeaders(),
      );
      if (response.data.success) {
        setCreds(response.data.credentials ?? []);
      } else {
        setActionError(response.data.error || 'Failed to reveal credentials');
      }
    } catch (err: any) {
      setActionError(
        err?.response?.data?.error || 'Failed to reveal credentials',
      );
    } finally {
      setCredLoading(false);
    }
  }, [commandId, authHeaders]);

  const revealCard = useCallback(
    async (requestAdditionalCard = false) => {
      try {
        setCardLoading(true);
        setActionError(null);
        const response = await api.post<CardDetailsResponse>(
          `/browser-agent/interventions/${encodeURIComponent(commandId)}/get-card-details`,
          requestAdditionalCard ? { requestAdditionalCard: true } : {},
          await authHeaders(),
        );
        setCard(response.data);
        setCardVisible(false);
      } catch (err: any) {
        setCard({
          success: false,
          error: cardRevealErrorMessage(err?.response?.data),
        });
      } finally {
        setCardLoading(false);
      }
    },
    [commandId, authHeaders],
  );

  const postCancelMfa = useCallback(
    async (requestId: string) =>
      api.post(
        `/browser-agent/interventions/${encodeURIComponent(commandId)}/cancel-mfa/${encodeURIComponent(requestId)}`,
        {},
        await authHeaders(),
      ),
    [commandId, authHeaders],
  );

  const beginMfaRequest = useCallback((kind: MfaKind) => {
    mfaSeq.current += 1;
    setMfa({ requestId: null, kind, status: 'requesting' });
    return mfaSeq.current;
  }, []);

  /** Apply a request POST response unless the operator dismissed it meanwhile. */
  const settleMfaRequest = useCallback(
    (seq: number, next: MfaState) => {
      if (mfaSeq.current !== seq) {
        if (next.requestId) {
          postCancelMfa(next.requestId).catch((err) =>
            console.error('Failed to cancel superseded MFA request', err),
          );
        }
        return;
      }
      if (next.status === 'error') {
        setMfa(next);
        return;
      }
      setMfa((prev) =>
        applyMfaRequestResponse(prev, {
          requestId: next.requestId,
          kind: next.kind,
          detail: next.detail ?? '',
        }),
      );
    },
    [postCancelMfa],
  );

  const requestLoginMfa = useCallback(async () => {
    setShowLoginPicker(false);
    const seq = beginMfaRequest('login');
    try {
      const response = await api.post(
        `/browser-agent/interventions/${encodeURIComponent(commandId)}/request-login-mfa`,
        { tfaType: loginTfaType },
        await authHeaders(),
      );
      const data = response.data as {
        success: boolean;
        requestId?: string;
        error?: string;
      };
      if (!data.success) {
        settleMfaRequest(seq, {
          requestId: null,
          kind: 'login',
          status: 'error',
          detail: data.error || 'MFA request failed',
        });
        return;
      }
      settleMfaRequest(seq, {
        requestId: data.requestId ?? null,
        kind: 'login',
        status: 'pending',
        detail: `Asked ${view?.intervention.requesterName || 'the user'} for their ${LOGIN_TFA_LABELS[loginTfaType].toLowerCase()}.`,
      });
    } catch (err: any) {
      settleMfaRequest(seq, {
        requestId: null,
        kind: 'login',
        status: 'error',
        detail: err?.response?.data?.error || 'MFA request failed',
      });
    }
  }, [
    commandId,
    loginTfaType,
    authHeaders,
    view,
    beginMfaRequest,
    settleMfaRequest,
  ]);

  const requestPayment3ds = useCallback(
    async (subtype?: 'cvv_entry') => {
      const seq = beginMfaRequest('payment_3ds');
      try {
        const response = await api.post(
          `/browser-agent/interventions/${encodeURIComponent(commandId)}/request-mfa`,
          { category: 'payment_3ds', ...(subtype ? { subtype } : {}) },
          await authHeaders(),
        );
        const data = response.data as {
          success: boolean;
          status?: string;
          requestId?: string;
          reason?: string;
          error?: string;
          routingToAdmin?: boolean;
        };
        if (!data.success || data.status === 'vcc-blocked') {
          settleMfaRequest(seq, {
            requestId: null,
            kind: 'payment_3ds',
            status: 'error',
            detail:
              data.reason ||
              data.error ||
              (subtype === 'cvv_entry'
                ? 'CVV request failed'
                : '3DS request failed'),
          });
          return;
        }
        settleMfaRequest(seq, {
          requestId: data.requestId ?? null,
          kind: 'payment_3ds',
          status: 'pending',
          detail: data.routingToAdmin
            ? 'Sent the 3-D Secure challenge to the card admin.'
            : subtype === 'cvv_entry'
              ? `Asked ${view?.intervention.requesterName || 'the user'} for the card's CVV.`
              : `Asked ${view?.intervention.requesterName || 'the user'} to complete 3-D Secure.`,
        });
      } catch (err: any) {
        settleMfaRequest(seq, {
          requestId: null,
          kind: 'payment_3ds',
          status: 'error',
          detail:
            err?.response?.data?.error ||
            (subtype === 'cvv_entry'
              ? 'CVV request failed'
              : '3DS request failed'),
        });
      }
    },
    [commandId, authHeaders, view, beginMfaRequest, settleMfaRequest],
  );

  /**
   * Cancel / dismiss the MFA card. A request the user may still act on
   * (pending / awaiting-user) is only removed once the backend has cancelled
   * it; if that fails the card stays up with the error so the operator knows
   * the user can still be prompted. Answered or failed requests, and requests
   * whose POST has not returned yet, are dismissed locally.
   */
  const cancelMfa = useCallback(async () => {
    if (!mfa || mfa.cancelling) return;
    const { requestId } = mfa;
    const liveOnServer =
      mfa.status === 'pending' || mfa.status === 'awaiting-user';
    if (requestId === null || !liveOnServer) {
      mfaSeq.current += 1;
      setMfa(null);
      return;
    }
    setMfa((prev) =>
      prev ? { ...prev, cancelling: true, cancelError: undefined } : prev,
    );
    try {
      await postCancelMfa(requestId);
      mfaSeq.current += 1;
      setMfa(null);
    } catch (err: any) {
      setMfa((prev) =>
        prev
          ? {
              ...prev,
              cancelling: false,
              cancelError: `Could not cancel — the user may still be prompted. ${
                err?.response?.data?.error || 'Try again.'
              }`,
            }
          : prev,
      );
    }
  }, [mfa, postCancelMfa]);

  const toggleField = useCallback((key: string) => {
    setVisibleFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const intervention = view?.intervention;
  const mandate = intervention?.mandate ?? null;
  const showCardButton = !!mandate;

  const spendLabel = useMemo(() => {
    if (!mandate) return null;
    if (!mandate.spendPermission) return mandate.brief;
    return `${mandate.spendCurrency ?? ''} ${mandate.spendPermission} — ${mandate.brief}`.trim();
  }, [mandate]);

  if (loading && !view) {
    return (
      <div className='intervention-operator-page'>
        <div className='iop-centered'>Loading intervention…</div>
      </div>
    );
  }

  if (loadError || !intervention) {
    return (
      <div className='intervention-operator-page'>
        <div className='iop-centered'>
          <p>{loadError || 'Intervention not found.'}</p>
          <Link to='/tasks' className='iop-link'>
            Back to Human Tasks
          </Link>
        </div>
      </div>
    );
  }

  if (resolvedAs) {
    const copy: Record<OutcomeKind, string> = {
      completed:
        'Handed back to the agent. It will check the page before carrying on.',
      could_not:
        "Told the agent you couldn't complete this. It will try another route or raise a task for a Dream worker.",
      feedback: 'Your guidance has been sent to the agent.',
    };
    return (
      <div className='intervention-operator-page'>
        <div className='iop-centered iop-resolved'>
          <h3>Done</h3>
          <p>{copy[resolvedAs]}</p>
          <Link to='/tasks' className='iop-link'>
            Back to Human Tasks
          </Link>
        </div>
      </div>
    );
  }

  const isBusy = submitting !== null;

  return (
    <div className='intervention-operator-page'>
      <header className='iop-header'>
        <div className='iop-header-main'>
          <Link to='/tasks' className='iop-back'>
            ← Tasks
          </Link>
          <div className='iop-title'>
            <span className='iop-kicker'>
              Browser intervention
              {intervention.attempt > 1 && (
                <span className='iop-attempt'>
                  retry #{intervention.attempt}
                </span>
              )}
              <span className='iop-age'>
                {formatAge(intervention.createdAt)}
              </span>
              {intervention.requesterName && (
                <span className='iop-requester'>
                  for {intervention.requesterName}
                </span>
              )}
            </span>
            <span className='iop-message'>{intervention.message}</span>
          </div>
        </div>
        <div className='iop-outcome'>
          {outcomeMode === 'idle' ? (
            <>
              <button
                className='iop-btn iop-btn--secondary'
                onClick={() => {
                  setNote('');
                  setOutcomeMode('feedback');
                }}
                disabled={isBusy}
              >
                Feedback
              </button>
              <button
                className='iop-btn iop-btn--danger'
                onClick={() => {
                  setNote('');
                  setOutcomeMode('could_not');
                }}
                disabled={isBusy}
              >
                Couldn't do
              </button>
              <button
                className='iop-btn iop-btn--primary'
                onClick={() => submitOutcome('completed')}
                disabled={isBusy}
              >
                {submitting === 'completed' ? 'Handing back…' : 'Done'}
              </button>
            </>
          ) : (
            <div className='iop-note'>
              <textarea
                autoFocus
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  outcomeMode === 'could_not'
                    ? 'What stopped you? (optional — the agent will quote this, so describe what the page showed)'
                    : 'Guidance for the agent, e.g. "use the returns tab, not orders"'
                }
                rows={2}
                maxLength={2000}
              />
              <div className='iop-note-actions'>
                <button
                  className='iop-btn iop-btn--secondary'
                  onClick={() => setOutcomeMode('idle')}
                  disabled={isBusy}
                >
                  Cancel
                </button>
                <button
                  className={`iop-btn ${outcomeMode === 'could_not' ? 'iop-btn--danger' : 'iop-btn--primary'}`}
                  onClick={() => submitOutcome(outcomeMode)}
                  disabled={isBusy}
                >
                  {submitting
                    ? 'Sending…'
                    : outcomeMode === 'could_not'
                      ? "Confirm couldn't do"
                      : 'Send feedback'}
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {actionError && <div className='iop-error'>{actionError}</div>}

      <div className='iop-body'>
        <main className='iop-live'>
          {intervention.provider === 'browserbase' && view?.liveViewUrl ? (
            <iframe
              title='Live browser'
              src={view.liveViewUrl}
              sandbox='allow-same-origin allow-scripts allow-forms allow-popups allow-modals'
              allow='clipboard-read; clipboard-write'
            />
          ) : intervention.provider === 'electron' && view?.liveViewUrl ? (
            <div className='iop-centered'>
              <p>This session runs in the desktop browser viewer.</p>
              <a
                className='iop-btn iop-btn--primary'
                href={view.liveViewUrl}
                target='_blank'
                rel='noopener noreferrer'
              >
                Open viewer
              </a>
            </div>
          ) : (
            <div className='iop-centered'>
              <p>
                The live browser is no longer reachable — the session may have
                been closed.
              </p>
              <button className='iop-btn iop-btn--secondary' onClick={load}>
                Retry
              </button>
            </div>
          )}
        </main>

        <aside className='iop-side'>
          <ComponentErrorBoundary
            componentName='Access'
            fallback={
              <section className='iop-panel'>
                <h4>Access</h4>
                <div className='iop-error'>
                  Could not display the stored login. Reload the page and try
                  again.
                </div>
              </section>
            }
          >
            <section className='iop-panel'>
              <h4>Access</h4>
              {intervention.hasCredentials ? (
                <button
                  className='iop-btn iop-btn--secondary iop-btn--block'
                  onClick={revealCredentials}
                  disabled={credLoading}
                >
                  {credLoading
                    ? 'Loading…'
                    : creds
                      ? 'Refresh credentials'
                      : 'Reveal credentials'}
                </button>
              ) : (
                <p className='iop-muted'>No stored login for this task.</p>
              )}
              {creds && creds.length === 0 && (
                <p className='iop-muted'>No credentials returned.</p>
              )}
              {creds?.map((cred) => (
                <div key={cred.credentialId} className='iop-card'>
                  <div className='iop-card-title'>
                    {cred.label}
                    {cred.site && (
                      <span className='iop-muted'> · {cred.site}</span>
                    )}
                  </div>
                  {cred.error && <div className='iop-error'>{cred.error}</div>}
                  {cred.fields &&
                    typeof cred.fields === 'object' &&
                    Object.entries(cred.fields).map(([key, raw]) => {
                      const value = credentialFieldText(raw);
                      const fieldKey = `${cred.credentialId}-${key}`;
                      const visible = visibleFields.has(fieldKey);
                      return (
                        <div key={key} className='iop-field'>
                          <span className='iop-field-label'>{key}</span>
                          <code className='iop-field-value'>
                            {visible ? value : maskValue(value)}
                          </code>
                          <button
                            className='iop-link-btn'
                            onClick={() => toggleField(fieldKey)}
                          >
                            {visible ? 'Hide' : 'Show'}
                          </button>
                          <button
                            className='iop-link-btn'
                            onClick={() =>
                              navigator.clipboard?.writeText(value)
                            }
                          >
                            Copy
                          </button>
                        </div>
                      );
                    })}
                </div>
              ))}
            </section>
          </ComponentErrorBoundary>

          <section className='iop-panel'>
            <h4>Payment</h4>
            {showCardButton ? (
              <>
                <p className='iop-muted'>{spendLabel}</p>
                <button
                  className='iop-btn iop-btn--secondary iop-btn--block'
                  onClick={() => revealCard(false)}
                  disabled={cardLoading}
                >
                  {cardLoading
                    ? 'Loading…'
                    : card?.success
                      ? 'Refresh card details'
                      : 'Card details'}
                </button>
                {card && !card.success && (
                  <div className='iop-error'>{card.error}</div>
                )}
                {card?.success && card.paymentMethod === 'merchant_stored' && (
                  <p className='iop-muted'>
                    {card.message}
                    {card.cardHint ? ` (${card.cardHint})` : ''}
                  </p>
                )}
                {card?.success &&
                  card.paymentMethod !== 'merchant_stored' &&
                  card.cardDetails && (
                    <div className='iop-card'>
                      <div className='iop-card-title'>
                        {card.paymentMethod === 'agent_card'
                          ? 'Dream-issued card'
                          : card.cardDetails.label || "User's card"}
                        <button
                          className='iop-link-btn'
                          onClick={() => setCardVisible((v) => !v)}
                        >
                          {cardVisible ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      {buildCardFieldRows(card.cardDetails).map(
                        ({ label, value }) => (
                          <div key={label} className='iop-field'>
                            <span className='iop-field-label'>{label}</span>
                            {value ? (
                              <>
                                <code className='iop-field-value'>
                                  {cardVisible ? value : maskValue(value)}
                                </code>
                                <button
                                  className='iop-link-btn'
                                  onClick={() =>
                                    navigator.clipboard?.writeText(value)
                                  }
                                >
                                  Copy
                                </button>
                              </>
                            ) : (
                              <span className='iop-muted'>
                                Not stored on this card
                              </span>
                            )}
                          </div>
                        ),
                      )}
                      {card.cardDetails.cvvMissing && (
                        <div className='iop-muted'>
                          CVV not on file — ask the user with the CVV needed
                          button
                        </div>
                      )}
                      {card.paymentMethod === 'agent_card' && (
                        <button
                          className='iop-link-btn'
                          onClick={() => revealCard(true)}
                          disabled={cardLoading}
                        >
                          Card declined? Issue a new one
                        </button>
                      )}
                    </div>
                  )}
              </>
            ) : (
              <p className='iop-muted'>No spending mandate on this task.</p>
            )}
          </section>

          <section className='iop-panel'>
            <h4>Verification codes</h4>
            {!mfa && (
              <div className='iop-mfa-buttons'>
                <button
                  className='iop-btn iop-btn--secondary iop-btn--block'
                  onClick={() => setShowLoginPicker((v) => !v)}
                >
                  Login MFA
                </button>
                {showLoginPicker && (
                  <div className='iop-mfa-picker'>
                    <select
                      value={loginTfaType}
                      onChange={(e) =>
                        setLoginTfaType(e.target.value as LoginTfaType)
                      }
                    >
                      {(
                        Object.entries(LOGIN_TFA_LABELS) as [
                          LoginTfaType,
                          string,
                        ][]
                      ).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <button
                      className='iop-btn iop-btn--primary'
                      onClick={requestLoginMfa}
                    >
                      Ask user
                    </button>
                  </div>
                )}
                {showCardButton && (
                  <>
                    <button
                      className='iop-btn iop-btn--secondary iop-btn--block'
                      onClick={() => requestPayment3ds()}
                    >
                      Payment 3DS
                    </button>
                    {mandate?.paymentMethod !== 'agent_card' && (
                      <button
                        className='iop-btn iop-btn--secondary iop-btn--block'
                        onClick={() => requestPayment3ds('cvv_entry')}
                      >
                        CVV needed
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            {mfa && (
              <div className={`iop-card iop-mfa iop-mfa--${mfa.status}`}>
                {mfa.status === 'requesting' && <p>Requesting…</p>}
                {mfa.status === 'pending' && (
                  <p>{mfa.detail} Waiting for their reply…</p>
                )}
                {mfa.status === 'awaiting-user' && (
                  <p>No code found automatically — asked the user directly.</p>
                )}
                {mfa.status === 'code' && (
                  <div className='iop-field'>
                    <span className='iop-field-label'>Code</span>
                    <code className='iop-field-value iop-field-value--big'>
                      {mfa.code}
                    </code>
                    <button
                      className='iop-link-btn'
                      onClick={() =>
                        navigator.clipboard?.writeText(mfa.code ?? '')
                      }
                    >
                      Copy
                    </button>
                  </div>
                )}
                {mfa.status === 'error' && (
                  <div className='iop-error'>{mfa.detail}</div>
                )}
                {mfa.cancelError && (
                  <div className='iop-error' role='alert'>
                    {mfa.cancelError}
                  </div>
                )}
                <button
                  className='iop-link-btn'
                  onClick={cancelMfa}
                  disabled={mfa.cancelling}
                >
                  {mfa.cancelling
                    ? 'Cancelling…'
                    : mfa.cancelError
                      ? 'Retry cancel'
                      : mfa.status === 'code' || mfa.status === 'error'
                        ? 'Dismiss'
                        : 'Cancel request'}
                </button>
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
};

export default InterventionOperatorPage;
